import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypeNarrativeOrderedListProps } from "./NarrativeOrderedListTypes";

/**
 * NarrativeOrderedList — a numbered list of action items. Each item pairs a
 * large ordinal (01, 02, …) with a title, an optional description, and an
 * optional call-to-action slot.
 *
 * Renders inner content only — it carries no surface, shadow, or accent of its
 * own. Drop it into a `NarrativeContainer` (see the stories / playground).
 *
 * Layout is driven by container queries, so it responds to the width the
 * component is actually given, not the viewport:
 * - `"vertical"` (default) → items stacked full-width, one per row.
 * - `"horizontal"` → items laid out as a row of equal columns once there is
 *   room, collapsing back to a stack when the container is narrow.
 *
 * Styled via `narrative-ordered-list.css` (Seeds CSS custom properties);
 * consumers import the classes through `@sproutsocial/racine/css/components`.
 * Overrides come via standard `className` / `style`.
 */
const NarrativeOrderedList = React.forwardRef<
  HTMLDivElement,
  TypeNarrativeOrderedListProps
>(({ items, orientation = "vertical", className, ...props }, ref) => {
  return (
    // The outer element establishes the query container. The ol inside switches
    // direction based on this element's width — a container query can only style
    // descendants of the container, never the container element itself.
    <div
      ref={ref}
      className={cn("seeds-narrative-ordered-list", className)}
      {...props}
    >
      <ol
        className={cn("seeds-narrative-ordered-list-track", {
          "seeds-narrative-ordered-list-horizontal":
            orientation === "horizontal",
        })}
      >
        {items.map((item, i) => (
          <li key={i} className="seeds-narrative-ordered-list-item">
            {/* Decorative — the ol/li already convey order to assistive tech, so
                hide the visible ordinal from it to avoid double-announcing. */}
            <span
              className="seeds-narrative-ordered-list-number"
              aria-hidden="true"
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="seeds-narrative-ordered-list-body">
              <p className="seeds-narrative-ordered-list-title">{item.title}</p>
              {item.description != null && (
                <p className="seeds-narrative-ordered-list-description">
                  {item.description}
                </p>
              )}
              {item.action != null && (
                <div className="seeds-narrative-ordered-list-action">
                  {item.action}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
});

NarrativeOrderedList.displayName = "NarrativeOrderedList";

export default NarrativeOrderedList;
